import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { IndianRupee, AlertTriangle, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";
import { appApi, inr, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const MerchantCodPanel = ({ userId, onSettled }) => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () =>
    appApi.get(`admin/cod/user/${userId}`).then(setData).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  if (!data) return <p className="px-5 py-6 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading orders…</p>;

  const orders = data.pending_orders || [];
  const chosen = orders.filter((o) => selected[o.id]);
  const chosenTotal = chosen.reduce((s, o) => s + (o.amount || 0), 0);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const settle = async () => {
    if (chosen.length === 0) {
      toast.error("Select at least one order.");
      return;
    }
    if (!window.confirm(`Settle ${inr(chosenTotal)} across ${chosen.length} order(s) for ${data.merchant?.name}?`)) return;
    setBusy(true);
    try {
      await appApi.post("admin/cod/settle", { order_ids: chosen.map((o) => o.id) });
      toast.success(`Settled ${inr(chosenTotal)} to ${data.merchant?.name}.`);
      setSelected({});
      load();
      onSettled();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 py-4 bg-paper border-t-2 border-obsidian" data-testid={`cod-panel-${userId}`}>
      {data.merchant?.bank_details?.account_number ? (
        <div className="mb-3 border-2 border-obsidian bg-white px-4 py-3 text-xs font-mono" data-testid={`cod-bank-${userId}`}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Payout account</p>
          <p className="mt-1 font-bold">{data.merchant.bank_details.account_holder_name}</p>
          <p className="text-zinc-600 mt-0.5">
            {data.merchant.bank_details.bank_name} · A/C {data.merchant.bank_details.account_number} · IFSC {data.merchant.bank_details.ifsc}
          </p>
        </div>
      ) : (
        <p className="text-xs font-mono font-bold uppercase tracking-widest text-blaze flex items-center gap-1.5 mb-3">
          <AlertTriangle className="w-3.5 h-3.5" /> Merchant bank details missing — settle after they add them
        </p>
      )}
      <table className="w-full text-sm">
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="border-b border-zinc-200 last:border-0" data-testid={`cod-order-${o.id}`}>
              <td className="py-2.5 pr-3 w-8">
                <button onClick={() => toggle(o.id)} aria-label={`Select ${o.awb}`} data-testid={`cod-select-${o.id}`} className="text-obsidian">
                  {selected[o.id] ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5 text-zinc-400" />}
                </button>
              </td>
              <td className="py-2.5 font-mono text-xs font-bold">{o.awb}</td>
              <td className="py-2.5 text-zinc-600">{o.customer_name} · {o.customer_phone}</td>
              <td className="py-2.5 font-mono text-xs text-zinc-500">{fmtDate(o.delivered_at)}</td>
              <td className="py-2.5 text-right font-mono text-xs font-bold">{inr(o.amount)}</td>
            </tr>
          ))}
          {orders.length === 0 && <tr><td className="py-6 text-sm text-zinc-500">No pending orders for this merchant.</td></tr>}
        </tbody>
      </table>
      {orders.length > 0 && (
        <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
          <p className="font-mono text-xs text-zinc-500" data-testid={`cod-selected-total-${userId}`}>
            {chosen.length} selected · {inr(chosenTotal)}
          </p>
          <button onClick={settle} disabled={busy || chosen.length === 0} data-testid={`cod-settle-${userId}`}
            className="bg-acid text-white text-xs font-bold uppercase tracking-widest px-6 py-3 border-2 border-obsidian shadow-[3px_3px_0px_0px_#0A0A0A] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150 disabled:opacity-50">
            {busy ? "Settling…" : "Settle Selected"}
          </button>
        </div>
      )}
    </div>
  );
};

export default function AdminCod() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");

  const load = () => appApi.get("admin/cod/pending").then(setData).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);

  if (!data)
    return <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400" data-testid="admin-cod-loading">Loading COD remittance…</p>;

  return (
    <div data-testid="admin-cod-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Admin</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">COD Remittance</h1>
        <p className="text-sm text-zinc-500 mt-2">Expand a merchant, select delivered COD orders, settle in one tap.</p>
      </div>

      <div className="grid grid-cols-2 border-2 border-obsidian bg-white max-w-2xl">
        <div className="p-6 border-r-2 border-obsidian" data-testid="admin-cod-merchants">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Merchants pending</p>
            <IndianRupee className="w-4 h-4 text-zinc-400" />
          </div>
          <p className="font-display font-black text-3xl tracking-tight mt-2">{data.total_merchants ?? 0}</p>
        </div>
        <div className="p-6" data-testid="admin-cod-amount">
          <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Total pending</p>
          <p className="font-display font-black text-3xl tracking-tight mt-2">
            {inr((data.merchants || []).reduce((s, m) => s + (m.pending_amount || 0), 0))}
          </p>
        </div>
      </div>

      <div className="border-2 border-obsidian bg-white" data-testid="admin-cod-table">
        {(data.merchants || []).map((m) => (
          <div key={m.user_id} className="border-b-2 border-obsidian last:border-0">
            <button
              onClick={() => setOpen(open === m.user_id ? "" : m.user_id)}
              data-testid={`cod-merchant-toggle-${m.user_id}`}
              className="w-full px-5 py-4 flex items-center justify-between gap-4 flex-wrap text-left hover:bg-acid/20 transition-colors duration-100"
            >
              <div>
                <p className="font-bold">{m.name} <span className="text-zinc-400 font-normal">· {m.business || m.email}</span></p>
                <p className="text-xs font-mono text-zinc-500 mt-0.5">
                  {m.pending_count} order(s) · oldest {fmtDate(m.oldest_delivered_at)}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {!m.bank_details && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase tracking-wider bg-blaze text-white px-2 py-0.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> Bank missing
                  </span>
                )}
                <span className="font-display font-black text-xl tracking-tight">{inr(m.pending_amount)}</span>
                <span className="font-mono text-xs font-bold uppercase tracking-widest text-blaze">
                  {open === m.user_id ? "Close" : "View & Settle"}
                </span>
              </div>
            </button>
            {open === m.user_id && <MerchantCodPanel userId={m.user_id} onSettled={load} />}
          </div>
        ))}
        {(data.merchants || []).length === 0 && (
          <p className="px-5 py-10 text-sm text-zinc-500" data-testid="admin-cod-empty">Nothing pending — all merchants settled.</p>
        )}
      </div>
    </div>
  );
}
